// VoiceRecorder Component - Record audio using shared useAudioRecording hook
// Supports multiple recordings with playback and delete

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAudioRecording } from '../../hooks/useAudioRecording';
import styles from './VoiceRecorder.module.css';

interface Recording {
  id: string;
  blob: Blob;
  duration: number;
  url: string;
}

interface VoiceRecorderProps {
  onRecordingsChange: (recordings: Recording[]) => void;
  recordings: Recording[];
  maxRecordings?: number;
}

export function VoiceRecorder({
  onRecordingsChange,
  recordings,
  maxRecordings = 10,
}: VoiceRecorderProps) {
  const {
    isRecording,
    duration,
    audioBlob,
    mimeType,
    startRecording,
    stopRecording,
    reset,
    error: recordingError,
  } = useAudioRecording();

  const [error, setError] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      recordings.forEach((r) => URL.revokeObjectURL(r.url));
    };
  }, []);

  // When a recording completes, add it to the list
  useEffect(() => {
    if (audioBlob && mimeType) {
      const url = URL.createObjectURL(audioBlob);
      const newRecording: Recording = {
        id: `rec-${Date.now()}`,
        blob: audioBlob,
        duration,
        url,
      };
      onRecordingsChange([...recordings, newRecording]);
      reset();
    }
  }, [audioBlob, mimeType]);

  const handleStartRecording = useCallback(async () => {
    if (recordings.length >= maxRecordings) {
      setError(`Maximum ${maxRecordings} recordings allowed`);
      return;
    }
    setError(null);
    await startRecording();
  }, [recordings.length, maxRecordings, startRecording]);

  const deleteRecording = useCallback(
    (id: string) => {
      const recording = recordings.find((r) => r.id === id);
      if (recording) {
        URL.revokeObjectURL(recording.url);
      }
      onRecordingsChange(recordings.filter((r) => r.id !== id));
    },
    [recordings, onRecordingsChange]
  );

  const playRecording = useCallback((recording: Recording) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(recording.url);
    audioRef.current = audio;
    setPlayingId(recording.id);

    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    audio.play();
  }, []);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingId(null);
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const displayError = error || recordingError;

  return (
    <div className={styles.voiceRecorder}>
      <div className={styles.recordingControls}>
        <button
          className={`${styles.recordButton} ${isRecording ? styles.recording : ''}`}
          onClick={isRecording ? stopRecording : handleStartRecording}
          disabled={recordings.length >= maxRecordings && !isRecording}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isRecording ? (
            <span className={styles.stopIcon}>&#9632;</span>
          ) : (
            <span className={styles.micIcon}>&#9679;</span>
          )}
        </button>

        <div className={styles.recordingInfo}>
          <div className={styles.recordingStatus}>
            {isRecording ? 'Recording...' : 'Ready to record'}
          </div>
          <div className={styles.recordingTime}>{formatTime(duration)}</div>
          {!isRecording && (
            <div className={styles.recordingHint}>
              Click to {recordings.length > 0 ? 'add another' : 'start'} recording
            </div>
          )}
        </div>
      </div>

      {displayError && <div className={styles.error}>{displayError}</div>}

      {recordings.length > 0 && (
        <div className={styles.recordingsList}>
          {recordings.map((recording, index) => (
            <div key={recording.id} className={styles.recordingItem}>
              <div className={styles.recordingIcon}>&#127908;</div>
              <div className={styles.recordingDetails}>
                <div className={styles.recordingName}>Recording {index + 1}</div>
                <div className={styles.recordingDuration}>
                  {formatTime(recording.duration)}
                </div>
              </div>
              <button
                className={styles.playButton}
                onClick={() =>
                  playingId === recording.id ? stopPlayback() : playRecording(recording)
                }
                aria-label={playingId === recording.id ? 'Stop' : 'Play'}
              >
                {playingId === recording.id ? '\u25A0' : '\u25B6'}
              </button>
              <button
                className={styles.deleteButton}
                onClick={() => deleteRecording(recording.id)}
                aria-label="Delete recording"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default VoiceRecorder;
